import { useCallback } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useMutation } from "@tanstack/react-query";
import { Trash2Icon } from "lucide-react";

import { formatDate } from "simplydo/core/dates";
import { asMutation } from "simplydo/core/query";
import { getUserDomain, isCommonDomain } from "simplydo/core/users";
import { Button } from "simplydo/react/ui/button";
import { Card, CardContent } from "simplydo/react/ui/card";
import {
  Checkbox,
  CheckboxContent,
  CheckboxHeader,
} from "simplydo/react/ui/checkbox";
import { Divider } from "simplydo/react/ui/divider";
import { H3, H4, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Message } from "simplydo/react/ui/message";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";

import api from "api";
import { useCurrentUser, userCache } from "utils/currentUser";
import util from "utils/utils";

import type {
  IdeaBusinessProfile,
  IdeaBusinessProfileDeleteResponse,
  IdeaBusinessProfileUpdate,
} from "../types";

type SettingsProps = {
  businessProfile: IdeaBusinessProfile;
  onCloseModal: () => void;
  updateProfile?: (businessProfile: IdeaBusinessProfileUpdate) => void;
  onDelete?: (ideaBusinessProfile: IdeaBusinessProfile) => void;
};

export const Settings = ({
  businessProfile,
  updateProfile,
  onDelete,
  onCloseModal,
}: SettingsProps) => {
  const user = useCurrentUser();
  const userDomain = user ? getUserDomain(user) : undefined;
  const { t } = useTranslation();

  const deleteProfileMutation = useMutation({
    mutationKey: ["delete", "users", "profile", businessProfile._id],
    mutationFn: asMutation(api.users.deleteProfile),
    onSuccess: (data: IdeaBusinessProfileDeleteResponse) => {
      const { success, reason } = data;
      // Attempt to delete the profile.
      // If the profile is attached to another idea, we alert the user, and don't continue with this action
      if (!success) {
        toast(reason ?? "Unable to delete profile");
      } else {
        api.invalidateQueries(api.users.getProfiles);
        api.setQueryData(
          api.users.getProfile,
          [businessProfile._id],
          undefined,
        );
        userCache.updatePermissions(businessProfile._id, []);
        onCloseModal();
        onDelete?.(businessProfile);
      }
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const deleteProfile = useCallback(() => {
    util
      .confirm(
        `Really delete the organisation profile ${businessProfile.name}?`,
        `Deleting the organisation profile ${businessProfile.name} is permanent and cannot be undone.`,
      )
      .then(() => {
        deleteProfileMutation.mutate([businessProfile._id]);
      })
      .catch(() => {});
  }, [businessProfile, deleteProfileMutation]);

  return (
    <div style={{ display: "flex", gap: 12, flexDirection: "column" }}>
      {userDomain && !isCommonDomain(userDomain) ? (
        <Card fluid>
          <CardContent>
            <H3>
              Settings
              <Subheader>
                Manage settings within this business profile.
              </Subheader>
            </H3>
            <Divider />
            <H4>
              Email domains
              <Subheader>
                Let other users automatically join this business profile when
                they sign up using the same email domain.
              </Subheader>
            </H4>
            <Checkbox
              toggle
              style={{ marginTop: 12 }}
              checked={
                !!businessProfile.emailDomains?.find(
                  (domain) => domain.domain === userDomain,
                )
              }
              onChange={(_e, d) => {
                const domains = businessProfile.emailDomains || [];

                updateProfile?.({
                  emailDomains: d.checked
                    ? [...domains, { domain: userDomain }]
                    : domains.filter((d) => d.domain !== userDomain),
                });
              }}
            >
              <CheckboxHeader>
                {`Open for other @${userDomain} users`}
              </CheckboxHeader>
              <CheckboxContent>
                Users with the same domain can join this profile.
              </CheckboxContent>
            </Checkbox>
            {businessProfile.emailDomains?.length ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell>Domain</TableHeaderCell>
                    <TableHeaderCell>Added on</TableHeaderCell>
                    <TableHeaderCell collapsing></TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {businessProfile.emailDomains.map((domain) => (
                    <TableRow key={domain.domain}>
                      <TableCell>@{domain.domain}</TableCell>
                      <TableCell>{formatDate(domain.addedAt)}</TableCell>
                      <TableCell>
                        <Button
                          onClick={() => {
                            util
                              .confirm(
                                "Remove domain",
                                `Really remove the domain @${domain}?`,
                              )
                              .then(() => {
                                updateProfile?.({
                                  emailDomains:
                                    businessProfile.emailDomains?.filter(
                                      (d) => d !== domain,
                                    ),
                                });
                              })
                              .catch(() => {});
                          }}
                          variant="outline"
                        >
                          <Icon icon={Trash2Icon} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : null}
          </CardContent>
        </Card>
      ) : null}
      <Card fluid>
        <CardContent>
          <H3>
            Delete profile
            <Subheader>
              Delete this profile if it is no longer needed.
            </Subheader>
          </H3>
          <Message color="orange">
            Deleting this profile will not remove it from any{" "}
            {t("generic.ideas")}. Once deleted, the profile cannot be edited or
            recovered.
          </Message>
          <Button
            disabled={
              deleteProfileMutation.isError ||
              !util.hasPermission(
                user,
                "ideaBusinessProfile.deleteProfile",
                businessProfile._id,
              )
            }
            onClick={() => {
              deleteProfile();
            }}
            color="red"
            variant="outline"
          >
            <Icon icon={Trash2Icon} />
            Delete profile
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};
